import { apiSlice } from "../../../features/api/apiSlice";

function pick(res: any) { return res?.data?.data ?? res?.data ?? res; }
function arr(res: any): any[] { const d = pick(res); return Array.isArray(d) ? d : []; }

export const eventsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({

    // ── Public ──────────────────────────────────────────────────────────────
    getPublicEvents: b.query<any[], void>({
      query: () => "/events",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "Events" as const, id: "LIST" }],
    }),

    getPublicEvent: b.query<any, string | number>({
      query: (id) => `/events/${id}`,
      transformResponse: (r) => pick(r),
      providesTags: (_, __, id) => [{ type: "Events" as const, id }],
    }),

    // ── Member Registration ──────────────────────────────────────────────────
    registerForEvent: b.mutation<any, { id: string | number; body: any }>({
      query: ({ id, body }) => ({ url: `/events/${id}/register`, method: "POST", body }),
      // Refetch the event itself too — its is_registered / my_registration flip.
      invalidatesTags: (_, __, { id }) => [
        { type: "MyEventRegistrations" as const, id: "LIST" },
        { type: "Events" as const, id: "LIST" },
        { type: "Events" as const, id },
      ],
    }),

    // Kicks off the OnePay charge for a pending registration. Returns the
    // gateway redirect payload — submit it with submitOnePayForm().
    initiateEventPayment: b.mutation<any, string | number>({
      query: (regId) => ({ url: `/my/events/${regId}/pay/initiate`, method: "POST" }),
      invalidatesTags: (_, __, id) => [
        { type: "MyEventRegistrations" as const, id },
        { type: "MyEventRegistrations" as const, id: "LIST" },
      ],
    }),

    // ── My Events ────────────────────────────────────────────────────────────
    getMyEventRegistrations: b.query<any[], void>({
      query: () => "/my/events",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "MyEventRegistrations" as const, id: "LIST" }],
    }),

    getMyEventRegistration: b.query<any, string | number>({
      query: (regId) => `/my/events/${regId}`,
      transformResponse: (r) => pick(r),
      providesTags: (_, __, id) => [{ type: "MyEventRegistrations" as const, id }],
    }),

    getMyEventInvoice: b.query<any, string | number>({
      query: (regId) => `/my/events/${regId}/invoice`,
      transformResponse: (r) => pick(r),
    }),

    // Only exists once the fee is confirmed — the API answers 402 until then.
    getMyEventReceipt: b.query<any, string | number>({
      query: (regId) => `/my/events/${regId}/receipt`,
      transformResponse: (r) => pick(r),
      providesTags: (_, __, id) => [{ type: "MyEventRegistrations" as const, id }],
    }),

    getMyEventAccessCard: b.query<any, string | number>({
      query: (regId) => `/my/events/${regId}/access-card`,
      transformResponse: (r) => pick(r),
    }),

    // The attendance certificate. 404 where the event issues none and 409
    // until CPD has been allocated for it, so the panel that asks for it has
    // to be ready for both — neither is an error worth showing as one.
    getMyEventCertificate: b.query<any, string | number>({
      query: (regId) => `/my/events/${regId}/certificate`,
      transformResponse: (r) => pick(r),
      providesTags: (_, __, id) => [{ type: "MyEventRegistrations" as const, id }],
    }),

    // ── Event documents ──────────────────────────────────────────────────────
    //
    // The files an event hands out — programme, papers, presentations. Only
    // published ones come back, and each carries `can_download`: a paper meant
    // for the delegates who were in the room is still LISTED to everyone else,
    // because knowing it exists is part of reading the programme.
    getEventDocuments: b.query<any[], string | number>({
      query: (eventId) => `/events/${eventId}/documents`,
      transformResponse: (r) => arr(r),
      providesTags: (_, __, eventId) => [{ type: "EventDocuments" as const, id: eventId }],
    }),

    // ── My CPD Ledger ────────────────────────────────────────────────────────
    getMyCpdLedger: b.query<{ data: any[]; total: number }, void>({
      query: () => "/my/cpd",
      transformResponse: (r: any) => ({ data: arr(r), total: r?.total ?? 0 }),
      providesTags: [{ type: "CpdLedger" as const, id: "MINE" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPublicEventsQuery,
  useGetPublicEventQuery,
  useRegisterForEventMutation,
  useInitiateEventPaymentMutation,
  useGetMyEventRegistrationsQuery,
  useLazyGetMyEventRegistrationsQuery,
  useGetMyEventRegistrationQuery,
  useGetMyEventInvoiceQuery,
  useGetMyEventReceiptQuery,
  useGetMyEventAccessCardQuery,
  useGetMyEventCertificateQuery,
  useGetEventDocumentsQuery,
  useGetMyCpdLedgerQuery,
} = eventsApi;
